import React, { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Users, Info } from "lucide-react";
import { levelIncome } from "@/lib/botMath";
import Stat from "@/components/calculator/Stat";

const bot = (n, dec = 2) => (isFinite(n) ? `${Number(n).toLocaleString(undefined, { maximumFractionDigits: dec })} BOT` : "—");
const usd = (n) => (isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—");

/**
 * BOT Staking Level Income — what your V-level pays on the BOT pledged beneath
 * you. CaryPact never handed us its percentage table, so the share is measured
 * from both sides landing in our own wallets, and the panel says so.
 */
export default function BotLevelView({ teamPledge, teamRatePct, sharePct, growthPct, months, botPrice, levelShare }) {
  const sim = useMemo(() => levelIncome({
    teamPledge, teamRatePct, sharePct, monthlyGrowthPct: growthPct, months,
  }), [teamPledge, teamRatePct, sharePct, growthPct, months]);

  const price = Number(botPrice) || 0;
  const M = Math.max(1, Math.round(Number(months) || 0));
  const obs = levelShare || {};

  return (
    <div className="space-y-5" data-testid="bot-level-view">
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="Level income a day" value={bot(sim.perDayBot, 4)} color="#0E7490"
          sub={`${usd(sim.perDayBot * price)} at today's BOT price`} testid="bot-level-kpi-day" />
        <Stat label="A month at this size" value={bot(sim.perMonthBot, 2)} color="#00863A"
          sub={`${usd(sim.perMonthBot * price)} · ${sharePct}% of what your team's pledges earn`} testid="bot-level-kpi-month" />
        <Stat label={`${M} months, team +${Number(growthPct) || 0}%/mo`} value={bot(sim.totalBot, 2)} color="#7C3AED"
          sub={`${usd(sim.totalBot * price)} · team ends at ${bot(sim.rows[M]?.teamPledge, 0)} pledged`} testid="bot-level-kpi-total" />
      </div>

      <div className="glass p-5" data-testid="bot-level-chart">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Level income as the team grows</div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <AreaChart data={sim.rows} margin={{ left: 8, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="botLvl" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0E7490" stopOpacity={0.32} /><stop offset="100%" stopColor="#0E7490" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,18,38,0.06)" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(m) => `m${m}`} />
              <YAxis tick={{ fontSize: 11 }} width={58}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Number(v).toFixed(v < 10 ? 1 : 0))} />
              <RTooltip formatter={(v, name) => [bot(v, 4), name]} labelFormatter={(m) => `Month ${m}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="perMonth" name="Level income that month" stroke="#0E7490" fill="url(#botLvl)" strokeWidth={2.5} />
              <Area type="monotone" dataKey="cumulative" name="Paid to date" stroke="#7C3AED" fill="none" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass p-4 flex items-start gap-3" data-testid="bot-level-observed">
        <Users size={15} style={{ color: "#00863A" }} className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-ink-2 leading-relaxed">
          {obs.pct != null ? (
            <>
              <b>Where the {sharePct}% comes from:</b> on your own wallets, BOT Staking Level Income has run at{" "}
              <b>{obs.pct}%</b> of the BOT staking rewards earned beneath it — {obs.min}% to {obs.max}% across{" "}
              {obs.days} day{obs.days === 1 ? "" : "s"} ({obs.from} → {obs.to}), about {bot(obs.level_bot_per_day, 3)} a day
              against {bot(obs.stake_bot_per_day, 3)} of team rewards. CaryPact doesn&apos;t publish its level table to
              us, so this is measured, not quoted — change the share if your dApp shows something different.
            </>
          ) : (
            <>
              No level-income days have been read into this account yet, so the share is yours to set. Open the
              Community Revenue screen in the dApp, read <b>BOT Staking Level Income</b> against the staking
              rewards below you, and type that percentage in — we won&apos;t guess it.
            </>
          )}
        </p>
      </div>

      <div className="glass p-4 flex items-start gap-3" data-testid="bot-level-disclaimer">
        <Info size={15} style={{ color: "#0E7490" }} className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-ink-2 leading-relaxed">
          Level income depends on your V-level and the levels below you, both of which move. This models one
          share against one team size — it is a planning tool, not a projection of what CaryPact will pay.
        </p>
      </div>
    </div>
  );
}
